import { useEffect, useState } from "react";
import { Loader2, X, UserRound, Crown } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

const TITLES = {
  users: "All user accounts",
  premium: "Premium subscribers",
  paywall: "Members who hit the paywall",
  conversions: "Conversions (started a paid subscription or trial)",
};

const HINTS = {
  paywall: "Tracked per member from today onward; guest paywall views stay anonymous.",
};

function Badge({ children, tone = "muted", testId }) {
  const cls = tone === "gold" ? "border-[#9B7E4E] text-[#9B7E4E]" : tone === "red" ? "border-[#9B3B3B] text-[#9B3B3B]" : "border-[#D9D4CC] text-[#7A756E]";
  return <span data-testid={testId} className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>;
}

function statusLine(u) {
  if (u.status === "trialing") return "Trial";
  if (u.status === "active") return u.cancel_at_period_end ? "Cancels at period end" : "Active";
  if (u.status === "canceled") return "Canceled";
  if (u.status === "past_due") return "Past due";
  return null;
}

export function AdminAccounts({ group, onClose }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get(`/admin/accounts?group=${group}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [group]);

  return (
    <div className="mt-4 border border-[#9B7E4E]/40 bg-[#FDFBF7] rounded-sm animate-fade-up" data-testid={`accounts-panel-${group}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#EAE6DF]">
        <div>
          <p className="font-heading text-lg text-[#2C2A29]">{TITLES[group]}{rows && <span className="font-body text-sm text-[#7A756E] ml-2" data-testid="accounts-count">· {rows.length}</span>}</p>
          {HINTS[group] && <p className="font-body text-xs text-[#7A756E] mt-0.5">{HINTS[group]}</p>}
        </div>
        <button data-testid="accounts-close" onClick={onClose} aria-label="Collapse" className="w-9 h-9 flex items-center justify-center rounded-sm text-[#7A756E] hover:bg-[#EAE6DF] transition-colors duration-300">
          <X className="w-5 h-5" />
        </button>
      </div>
      {rows === null ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 text-[#2C2A29] animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <p className="font-decree text-lg text-[#4A4744] px-5 py-8" data-testid="accounts-empty">No accounts in this group yet.</p>
      ) : (
        <ul className="divide-y divide-[#EAE6DF] max-h-[28rem] overflow-y-auto" data-testid="accounts-list">
          {rows.map((u) => {
            const st = statusLine(u);
            return (
              <li key={u.id} data-testid={`account-row-${u.id}`} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-10 h-10 rounded-full overflow-hidden border border-[#D9D4CC] bg-[#EAE6DF] flex items-center justify-center shrink-0">
                  {u.avatar_url ? <img src={mediaUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserRound className="w-5 h-5 text-[#B7AE9F]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-body text-sm text-[#2C2A29] truncate">{u.name || "—"}</p>
                    {u.role === "admin" && <Badge>Admin</Badge>}
                    {u.tier === "premium" && <Badge tone="gold"><Crown className="w-3 h-3 inline -mt-0.5 mr-0.5" />{u.gift_until && !u.plan ? "Gifted" : "Premium"}</Badge>}
                    {st && <Badge tone={u.status === "canceled" || u.status === "past_due" ? "red" : "muted"}>{st}</Badge>}
                  </div>
                  <p className="font-body text-xs text-[#7A756E] truncate mt-0.5">
                    {u.email}
                    {u.plan && ` · ${u.plan === "premium_annual" ? "Annual" : "Monthly"}`}
                    {group === "paywall" && u.paywall_views != null && ` · ${u.paywall_views} view${u.paywall_views === 1 ? "" : "s"}`}
                    {group === "paywall" && u.trial_used && " · trial started"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <Badge>{u.provider}</Badge>
                  <p className="font-body text-[11px] text-[#B7AE9F] mt-1">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
